/**
 * Portail Maitre d'oeuvre (MOE) — Juin 2026.
 * Vue type client du projet attribue : infos, timeline, planning & budget travaux,
 * documents partages. PAS d'analyses projets ni de messagerie client.
 * + Comptes rendus de reunion : creation (email auto charge de projet + CEO)
 *   et partage au client (portail + email).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import WorksScheduleSection from '../components/WorksScheduleSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEUR = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('fr-FR')} €`);
const fmtDate = (s) => { if (!s) return null; try { return new Date(s).toLocaleDateString('fr-FR'); } catch { return s; } };

const PhotoGrid = ({ photos, onDelete }) => (
  (photos || []).length > 0 ? (
    <div className="flex flex-wrap gap-2 mt-2">
      {photos.map(ph => (
        <div key={ph.photo_id} className="relative group">
          <a href={`${API}/moe/photos/${ph.photo_id}`} target="_blank" rel="noreferrer">
            <img src={`${API}/moe/photos/${ph.photo_id}`} alt={ph.filename}
                 className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                 data-testid={`cr-photo-${ph.photo_id}`} />
          </a>
          {onDelete && (
            <button onClick={() => onDelete(ph)} title="Supprimer la photo"
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] leading-5 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`cr-photo-delete-${ph.photo_id}`}>✕</button>
          )}
        </div>
      ))}
    </div>
  ) : null
);

const MeetingReports = ({ reports, onCreated, onShareToggle }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', meeting_date: '', participants: '', content: '' });
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openCr, setOpenCr] = useState(null);
  const [addingPhotosTo, setAddingPhotosTo] = useState(null);

  const submit = async () => {
    if (!form.title.trim() || !form.content.trim()) { alert('Titre et contenu obligatoires.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title.trim());
      fd.append('content', form.content.trim());
      if (form.meeting_date) fd.append('meeting_date', `${form.meeting_date}T12:00:00Z`);
      if (form.participants.trim()) fd.append('participants', form.participants.trim());
      photos.forEach(f => fd.append('files', f));
      await axios.post(`${API}/moe/reports`, fd);
      setForm({ title: '', meeting_date: '', participants: '', content: '' });
      setPhotos([]);
      setShowForm(false);
      onCreated();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur lors de la création'); }
    finally { setSaving(false); }
  };

  const addPhotos = async (cr, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setAddingPhotosTo(cr.cr_id);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      await axios.post(`${API}/moe/reports/${cr.cr_id}/photos`, fd);
      onCreated();
    } catch (e) { alert(e.response?.data?.detail || "Erreur lors de l'ajout des photos"); }
    finally { setAddingPhotosTo(null); }
  };

  const deletePhoto = async (cr, ph) => {
    if (!window.confirm('Supprimer cette photo du compte rendu ?')) return;
    try { await axios.delete(`${API}/moe/reports/${cr.cr_id}/photos/${ph.photo_id}`); onCreated(); }
    catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="realease-card p-6" data-testid="moe-reports-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">Comptes rendus de réunion</h3>
          <p className="text-xs text-gray-500">À chaque CR créé, l'équipe RealEase est automatiquement notifiée par email.</p>
        </div>
        <button onClick={() => setShowForm(s => !s)}
                className="text-sm px-3 py-1.5 rounded-lg bg-[#13736D] hover:bg-[#0f5f5a] text-white font-semibold"
                data-testid="moe-add-cr-btn">{showForm ? 'Annuler' : '+ Compte rendu'}</button>
      </div>

      {showForm && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-2 mb-4" data-testid="moe-cr-form">
          <input type="text" placeholder="Titre du compte rendu *" value={form.title}
                 onChange={e => setForm({ ...form, title: e.target.value })}
                 className="realease-input text-sm w-full" data-testid="moe-cr-title-input" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-gray-400 font-semibold">Date de réunion</label>
              <input type="date" value={form.meeting_date}
                     onChange={e => setForm({ ...form, meeting_date: e.target.value })}
                     className="realease-input text-sm w-full" data-testid="moe-cr-date-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-gray-400 font-semibold">Participants (optionnel)</label>
              <input type="text" placeholder="Ex. MOE, électricien, plombier" value={form.participants}
                     onChange={e => setForm({ ...form, participants: e.target.value })}
                     className="realease-input text-sm w-full" data-testid="moe-cr-participants-input" />
            </div>
          </div>
          <textarea placeholder="Contenu du compte rendu *" value={form.content} rows={6}
                    onChange={e => setForm({ ...form, content: e.target.value })}
                    className="realease-input text-sm w-full resize-y" data-testid="moe-cr-content-input" />
          <div>
            <label className="text-[10px] uppercase text-gray-400 font-semibold">Photos de chantier (plusieurs à la fois)</label>
            <input type="file" accept="image/*" multiple
                   onChange={e => setPhotos(Array.from(e.target.files || []))}
                   className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-[#13736D]/10 file:text-[#13736D] file:font-semibold file:text-xs"
                   data-testid="moe-cr-photos-input" />
            {photos.length > 0 && <p className="text-xs text-gray-500 mt-1">{photos.length} photo(s) sélectionnée(s) — jointes à l'email et déposées dans le Drive du projet</p>}
          </div>
          <button onClick={submit} disabled={saving}
                  className="w-full text-sm py-2 rounded-lg bg-[#13736D] hover:bg-[#0f5f5a] text-white font-semibold disabled:opacity-50"
                  data-testid="moe-cr-submit-btn">{saving ? 'Envoi…' : 'Créer le compte rendu (équipe notifiée par email)'}</button>
        </div>
      )}

      {reports.length === 0 ? (
        <p className="text-sm text-gray-400 italic" data-testid="moe-cr-empty">Aucun compte rendu pour le moment.</p>
      ) : (
        <div className="space-y-2">
          {reports.map(cr => (
            <div key={cr.cr_id} className="border border-gray-100 rounded-lg" data-testid={`moe-portal-cr-${cr.cr_id}`}>
              <button onClick={() => setOpenCr(openCr === cr.cr_id ? null : cr.cr_id)}
                      className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 text-left">
                <span className="text-sm font-medium text-gray-800 truncate">{cr.title}</span>
                <span className="text-xs text-gray-400 shrink-0 ml-2">{fmtDate(cr.meeting_date) || fmtDate(cr.created_at)}</span>
              </button>
              {openCr === cr.cr_id && (
                <div className="px-3 pb-3">
                  {cr.participants && <p className="text-xs text-gray-500 mb-1">Participants : {cr.participants}</p>}
                  <p className="whitespace-pre-wrap text-[13px] text-gray-700 bg-gray-50 rounded p-2.5">{cr.content}</p>
                  <PhotoGrid photos={cr.photos} onDelete={(ph) => deletePhoto(cr, ph)} />
                  <label className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#13736D] cursor-pointer hover:underline">
                    <input type="file" accept="image/*" multiple className="hidden"
                           onChange={e => { addPhotos(cr, e.target.files); e.target.value = ''; }}
                           data-testid={`moe-cr-addphotos-${cr.cr_id}`} />
                    {addingPhotosTo === cr.cr_id ? 'Ajout en cours…' : '+ Ajouter des photos'}
                  </label>
                </div>
              )}
              <label className={`flex items-center gap-2 cursor-pointer px-3 py-2 border-t border-gray-100 ${cr.shared_with_client ? 'bg-emerald-50' : ''}`}>
                <input type="checkbox" checked={!!cr.shared_with_client}
                       onChange={e => onShareToggle(cr, e.target.checked)}
                       data-testid={`moe-cr-share-${cr.cr_id}`} />
                <span className={`text-xs font-semibold ${cr.shared_with_client ? 'text-emerald-700' : 'text-gray-600'}`}>
                  Mettre à disposition du client (portail + email)
                  {cr.client_emailed_at && <span className="font-normal text-emerald-600"> — envoyé au client le {fmtDate(cr.client_emailed_at)}</span>}
                </span>
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function MoePortalPage({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [budget, setBudget] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [d, b, r] = await Promise.all([
        axios.get(`${API}/moe/my-project`).catch(() => ({ data: null })),
        axios.get(`${API}/moe/works-budget`).catch(() => ({ data: null })),
        axios.get(`${API}/moe/reports`).catch(() => ({ data: { items: [] } })),
      ]);
      setData(d.data); setBudget(b.data); setReports(r.data?.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleShare = async (cr, shared) => {
    try {
      const r = await axios.put(`${API}/moe/reports/${cr.cr_id}/share`, { shared });
      if (r.data.emailed_client) alert('Le compte rendu a été envoyé par email au client et est visible sur son portail.');
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-t-[#13736D] border-gray-200 rounded-full animate-spin"></div></div>;

  const p = data?.project;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="moe-portal">
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/realease-logo.jpg" alt="RealEase" className="h-9 object-contain" />
          <span className="text-xs font-semibold px-2 py-0.5 rounded bg-[#13736D]/10 text-[#13736D]">Espace Maître d'œuvre</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">{user?.name}</span>
          <button onClick={onLogout} className="text-sm text-gray-500 hover:text-gray-700" data-testid="moe-logout-btn">Déconnexion</button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-6 lg:p-8">
        {!p ? (
          <div className="text-center py-20" data-testid="moe-no-project">
            <p className="text-gray-500 text-lg">{data?.message || 'Aucun projet ne vous est attribué pour le moment.'}</p>
            <p className="text-gray-400 text-sm mt-2">Contactez l'équipe RealEase.</p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900" data-testid="moe-project-name">{p.name}</h1>
              <div className="flex items-center gap-3 mt-2 text-gray-600 text-sm">
                <span>{p.status_label}</span>
                {p.geographic_zone && <span>• {p.geographic_zone}</span>}
                {p.client_name && <span>• Client : {p.client_name}</span>}
              </div>
            </div>

            <div className="realease-card p-6 mb-6" data-testid="moe-timeline">
              <h3 className="font-semibold text-gray-900 mb-4">Avancement du projet</h3>
              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                {(data.timeline || []).map((t, i) => (
                  <div key={i} className="flex items-center">
                    <div className={`flex flex-col items-center ${t.current ? 'scale-110' : ''}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${t.completed ? 'bg-green-500 text-white' : t.current ? 'bg-[#13736D] text-white ring-4 ring-[#13736D]/20' : 'bg-gray-200 text-gray-500'}`}>
                        {t.completed ? '\u2713' : i + 1}
                      </div>
                      <span className={`text-xs mt-1 whitespace-nowrap ${t.current ? 'font-semibold text-[#13736D]' : 'text-gray-500'}`}>{t.phase}</span>
                    </div>
                    {i < data.timeline.length - 1 && <div className={`w-8 h-0.5 mx-1 ${t.completed ? 'bg-green-500' : 'bg-gray-200'}`}></div>}
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-1"><span className="text-gray-600">Progression globale</span><span className="font-bold">{p.progress}%</span></div>
                <div className="progress-bar h-3"><div className="progress-bar-fill" style={{ width: `${p.progress}%` }}></div></div>
              </div>
            </div>

            <div className="mb-6">
              <MeetingReports reports={reports} onCreated={fetchAll} onShareToggle={toggleShare} />
            </div>

            <div className="mb-6">
              <WorksScheduleSection projectId={p.project_id} mode="client" endpoint={`${API}/moe/works-schedule`} />
            </div>

            {budget?.summary && (
              <div className="realease-card p-6 mb-6" data-testid="moe-budget">
                <h3 className="font-semibold text-gray-900 mb-4">Budget travaux</h3>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="rounded-lg bg-gray-50 p-3 text-center"><p className="text-xs text-gray-500">Total engagé TTC</p><p className="font-bold text-gray-900">{fmtEUR(budget.summary.travaux_total_ttc)}</p></div>
                  <div className="rounded-lg bg-emerald-50 p-3 text-center"><p className="text-xs text-gray-500">Payé</p><p className="font-bold text-emerald-700">{fmtEUR(budget.summary.travaux_paye_ttc)}</p></div>
                  <div className="rounded-lg bg-amber-50 p-3 text-center"><p className="text-xs text-gray-500">Reste à payer</p><p className="font-bold text-amber-700">{fmtEUR(budget.summary.travaux_reste_a_payer_ttc)}</p></div>
                </div>
                {(budget.interventions || []).length > 0 && (
                  <div className="space-y-1.5">
                    {budget.interventions.map(it => (
                      <div key={it.id} className="flex items-center justify-between text-sm border-b border-gray-50 py-1.5">
                        <span className="text-gray-700 truncate">{it.intervenant}{it.description ? ` — ${it.description}` : ''}</span>
                        <span className="shrink-0 ml-2 flex items-center gap-2">
                          <span className="font-semibold">{fmtEUR(it.cost_ttc)}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${it.paid ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{it.paid ? 'Payé' : 'À payer'}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="realease-card p-6" data-testid="moe-documents">
              <h3 className="font-semibold text-gray-900 mb-4">Documents partagés</h3>
              {(data.documents || []).length > 0 ? (
                <div className="space-y-2">
                  {data.documents.map(doc => (
                    <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3 min-w-0">
                        <svg className="w-5 h-5 text-[#13736D] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                        <p className="text-sm font-medium truncate">{doc.filename}</p>
                      </div>
                      {doc.drive_url && <a href={doc.drive_url} target="_blank" rel="noreferrer" className="text-[#13736D] text-xs font-semibold hover:underline shrink-0 ml-2">Ouvrir</a>}
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-gray-400 italic">Aucun document partagé.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
